/** 手書きストロークの描画とPNG書き出し（仕様書 §10）。 */

import {
  DRAWING_HEIGHT,
  DRAWING_WIDTH,
  type Stroke,
  type StrokeDoc,
  SCHEMA_VERSION,
} from '../domain/types';

/**
 * 書き出しPNGの倍率。論理サイズ 1024×1448 に対して 2048×2896 で書き出す。
 * 1倍だとA4相当で約90dpiしかなく、文字が潰れて読みにくい。
 */
export const EXPORT_SCALE = 2;
export const EXPORT_WIDTH = DRAWING_WIDTH * EXPORT_SCALE;
export const EXPORT_HEIGHT = DRAWING_HEIGHT * EXPORT_SCALE;

export function emptyStrokeDoc(): StrokeDoc {
  return {
    schemaVersion: SCHEMA_VERSION,
    width: DRAWING_WIDTH,
    height: DRAWING_HEIGHT,
    background: '#FFFFFF',
    strokes: [],
  };
}

/** 筆圧に応じた線幅。筆圧非対応の入力は 0.5 として扱う。 */
export function lineWidth(size: number, pressure: number): number {
  return size * (0.35 + 0.65 * pressure);
}

/**
 * 1本のストロークを描く。
 * 点をそのまま結ばず、隣り合う点の中点を通る二次ベジェで平滑化する。
 * 筆圧で線幅が変わるため、区間ごとに個別のパスとして描く。
 */
export function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
  const pts = stroke.points;
  if (pts.length === 0) return;

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalCompositeOperation =
    stroke.tool === 'eraser' ? 'destination-out' : 'source-over';
  ctx.strokeStyle = stroke.tool === 'eraser' ? '#000000' : stroke.color;

  // 点が1つだけなら丸を打つ
  if (pts.length === 1) {
    const [x, y, p] = pts[0];
    ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath();
    ctx.arc(x, y, lineWidth(stroke.size, p) / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const midPrev =
      i === 1 ? [prev[0], prev[1]] : [(pts[i - 2][0] + prev[0]) / 2, (pts[i - 2][1] + prev[1]) / 2];
    const midCur =
      i === pts.length - 1 ? [cur[0], cur[1]] : [(prev[0] + cur[0]) / 2, (prev[1] + cur[1]) / 2];

    ctx.beginPath();
    ctx.lineWidth = lineWidth(stroke.size, (prev[2] + cur[2]) / 2);
    ctx.moveTo(midPrev[0], midPrev[1]);
    ctx.quadraticCurveTo(prev[0], prev[1], midCur[0], midCur[1]);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * 全ストロークを「透明な層」に描く。背景は塗らない。
 *
 * 消しゴムは destination-out で描くため、背景を先に塗ってしまうと
 * 背景ごと削り取られ、書き出したPNGに透明な穴が空いてしまう。
 * 背景との合成は最後に行うこと（exportPng を参照）。
 */
export function renderStrokes(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strokes: Stroke[],
  scale = 1,
  offsetX = 0,
  offsetY = 0,
): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width * scale, height * scale);
  ctx.restore();

  // ストロークは論理座標（1024×1448）で持っているので、変換で拡大して描く。
  // ラスタを拡大すると滲むが、ベクタのまま高解像度で描けば鮮明なまま。
  ctx.save();
  ctx.setTransform(scale, 0, 0, scale, -offsetX * scale, -offsetY * scale);
  for (const s of strokes) drawStroke(ctx, s);
  ctx.restore();
}

/** 描かれている範囲（論理座標）。消しゴムだけの部分は含めない。 */
export function inkBounds(
  strokes: Stroke[],
): { x: number; y: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const s of strokes) {
    if (s.tool === 'eraser') continue; // 消した跡は「描かれた範囲」ではない
    for (const [x, y, p] of s.points) {
      const r = lineWidth(s.size, p) / 2;
      if (x - r < minX) minX = x - r;
      if (y - r < minY) minY = y - r;
      if (x + r > maxX) maxX = x + r;
      if (y + r > maxY) maxY = y + r;
    }
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** 切り詰めるときに描画の周りに残す余白（論理px）。 */
const CROP_MARGIN = 48;
/** 切り詰め後の最小サイズ（論理px）。点ひとつでも極端に小さい画像にしない。 */
const CROP_MIN = 240;

/**
 * 保存用のPNGを書き出す。背景は不透明の白。既定で論理サイズの2倍。
 *
 * 既定で「描かれている範囲」に切り詰める。A4一枚の白紙のまま書き出すと、
 * 一覧のサムネイルも詳細も拡大表示もほとんど余白になり、何が描いてあるのか
 * 分からなくなるため。ストロークは全面の座標のまま保持するので、再編集には影響しない。
 */
export async function exportPng(
  doc: StrokeDoc,
  scale = EXPORT_SCALE,
  crop = true,
): Promise<{ blob: Blob; width: number; height: number }> {
  let srcX = 0;
  let srcY = 0;
  let srcW = doc.width;
  let srcH = doc.height;

  if (crop) {
    const b = inkBounds(doc.strokes);
    if (b) {
      const w = Math.max(b.width + CROP_MARGIN * 2, CROP_MIN);
      const h = Math.max(b.height + CROP_MARGIN * 2, CROP_MIN);
      // 中心を保ったまま広げ、用紙からはみ出さないよう収める
      srcW = Math.min(w, doc.width);
      srcH = Math.min(h, doc.height);
      srcX = Math.min(Math.max(b.x + b.width / 2 - srcW / 2, 0), doc.width - srcW);
      srcY = Math.min(Math.max(b.y + b.height / 2 - srcH / 2, 0), doc.height - srcH);
    }
  }

  const width = Math.round(srcW * scale);
  const height = Math.round(srcH * scale);

  // 1) ストロークを透明な層に描く（消しゴムはこの層の中だけを削る）
  const layer = document.createElement('canvas');
  layer.width = width;
  layer.height = height;
  const layerCtx = layer.getContext('2d');
  if (!layerCtx) throw new Error('キャンバスを初期化できませんでした');
  renderStrokes(layerCtx, srcW, srcH, doc.strokes, scale, srcX, srcY);

  // 2) 不透明な背景の上に重ねる
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('キャンバスを初期化できませんでした');
  ctx.fillStyle = doc.background;
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(layer, 0, 0);

  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('画像の書き出しに失敗しました');
  return { blob, width, height };
}
